import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Linkedin, Music2, Copy, Check, Wand2 } from "lucide-react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Toaster, toast } from "sonner";

const PLATFORMS = [
    { key: "instagram", label: "Instagram", icon: Instagram, max: 150 },
    { key: "linkedin", label: "LinkedIn", icon: Linkedin, max: 300 },
    { key: "tiktok", label: "TikTok", icon: Music2, max: 80 },
];

export default function BioGenerator() {
    const [platform, setPlatform] = useState("instagram");
    const [niche, setNiche] = useState("");
    const [goal, setGoal] = useState("creator");
    const [description, setDescription] = useState("");
    const [bio, setBio] = useState("");
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [history, setHistory] = useState([]);

    const loadHistory = async () => {
        const { data } = await api.get("/bios");
        setHistory(data || []);
    };

    useEffect(() => {
        loadHistory();
    }, []);

    const generate = async () => {
        if (description.length < 20) return toast.error("Description min 20 chars");
        setLoading(true);
        setBio("");
        try {
            const { data } = await api.post("/ai/generate-bio", { platform, niche, goal, description });
            setBio(data.content);
            loadHistory();
            toast.success("Bio generated");
        } catch (err) {
            const status = err?.response?.status;
            if (status === 429) {
                toast.error("🤖 AI engine is on pause — rate limit reached. Try again in a few minutes.", {
                    duration: 60000,
                });
            } else {
                toast.error(err?.response?.data?.detail || err?.response?.data?.error?.message || "Generation failed");
            }
        } finally {
            setLoading(false);
        }
    };

    const copy = async () => {
        await navigator.clipboard.writeText(bio);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const current = PLATFORMS.find((p) => p.key === platform);

    return (
        <Layout>
            <Toaster theme="light" position="top-center" />
            <div className="mb-8">
                <div className="font-jetbrains-mono text-xs tracking-[0.3em] text-aura-violet mb-2">
                    // BIO_GENERATOR / NEURAL FORGE
                </div>
                <h1 className="font-syne text-4xl sm:text-5xl font-bold text-aura-text">Generate <span className="text-grad-violet">magnetic</span> bios.</h1>
            </div>

            <div className="grid lg:grid-cols-[1fr_400px] gap-6">
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass p-6 lg:p-8 space-y-5" data-testid="bio-generator-card">
                    <div className="flex gap-2 border-b border-aura-violet/15">
                        {PLATFORMS.map((p) => (
                            <button
                                key={p.key}
                                onClick={() => setPlatform(p.key)}
                                className={`flex items-center gap-2 px-4 py-3 -mb-px text-sm font-dm-sans transition ${
                                    platform === p.key
                                        ? "text-aura-violet border-b-2 border-aura-violet font-semibold"
                                        : "text-aura-body hover:text-aura-text"
                                }`}
                                data-testid={`bio-tab-${p.key}`}
                            >
                                <p.icon className="w-4 h-4" />
                                {p.label}
                            </button>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">Goal</label>
                            <select value={goal} onChange={(e) => setGoal(e.target.value)} className="mt-1.5 w-full bg-aura-input text-aura-text border border-aura-violet/15 rounded-lg px-4 py-3 text-sm focus:border-aura-violet focus:outline-none" data-testid="bio-goal-select">
                                <option value="creator">Creator</option>
                                <option value="influencer">Influencer</option>
                                <option value="professional">Professional</option>
                                <option value="entrepreneur">Entrepreneur</option>
                            </select>
                        </div>
                        <div>
                            <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">Niche</label>
                            <input value={niche} onChange={(e) => setNiche(e.target.value)} placeholder="e.g. AI productivity" className="mt-1.5 w-full bg-aura-input text-aura-text border border-aura-violet/15 rounded-lg px-4 py-3 text-sm focus:border-aura-violet focus:outline-none" data-testid="bio-niche-input" />
                        </div>
                    </div>

                    <div>
                        <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">Describe yourself & what you do</label>
                        <textarea
                            rows={4}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="mt-1.5 w-full bg-aura-input text-aura-text border border-aura-violet/15 rounded-lg px-4 py-3 text-sm focus:border-aura-violet focus:outline-none"
                            data-testid="bio-description-input"
                        />
                    </div>

                    <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={generate}
                        disabled={loading}
                        className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-aura-violet to-aura-glow text-white font-dm-sans font-medium glow-violet-soft btn-ripple flex items-center justify-center gap-2 disabled:opacity-60"
                        data-testid="bio-generate-btn"
                    >
                        {loading ? <><div className="cyber-spinner !w-4 !h-4" /> Forging…</> : <><Wand2 className="w-4 h-4" /> Generate {current.label} bio</>}
                    </motion.button>

                    {bio && (
                        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white border-2 border-aura-violet/30 rounded-xl p-5 shadow-[0_10px_30px_-10px_rgba(124,58,237,0.3)]" data-testid="bio-output">
                            <div className="flex items-center justify-between mb-3">
                                <span className="font-jetbrains-mono text-[10px] uppercase tracking-wider text-aura-violet font-semibold">// {current.label} bio</span>
                                <span className="font-jetbrains-mono text-[10px] text-aura-muted">{bio.length}/{current.max}</span>
                            </div>
                            <p className="font-dm-sans text-aura-text whitespace-pre-line leading-relaxed">{bio}</p>
                            <button onClick={copy} className="mt-4 flex items-center gap-2 text-xs text-aura-violet hover:text-aura-glow transition font-semibold" data-testid="bio-copy-btn">
                                {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy bio</>}
                            </button>
                        </motion.div>
                    )}
                </motion.div>

                <motion.aside initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass p-6 lg:sticky lg:top-8 self-start" data-testid="bio-history">
                    <h3 className="font-syne text-lg font-semibold mb-4 text-aura-text">History</h3>
                    {history.length === 0 ? (
                        <p className="text-sm text-aura-body">No bios yet.</p>
                    ) : (
                        <div className="space-y-3 max-h-[500px] overflow-y-auto">
                            {history.map((b) => (
                                <div key={b.id} className="border border-aura-violet/10 bg-white rounded-lg p-3">
                                    <div className="font-jetbrains-mono text-[10px] uppercase tracking-wider text-aura-violet mb-1 font-semibold">{b.platform}</div>
                                    <div className="text-xs text-aura-text leading-relaxed">{b.content}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </motion.aside>
            </div>
        </Layout>
    );
}

